import { DataTable } from '@/components/table/DataTable';
import { Button } from '@/components/ui/button';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import { Text } from '@/components/ui/text';
import { Textarea } from '@/components/ui/textarea';
import { useLiteratureReviewSearchesService } from '@/services/literatureReviews';
import { cn } from '@/utils/classUtils';
import { type Database } from '@ditto/shared';
import { getPaginationRowModel } from '@tanstack/react-table';
import { Search } from 'lucide-react';
import { useMemo, useState, type FC } from 'react';
import { useParams } from 'react-router-dom';
import { useDatabaseColumns } from '../columns';
import { DatabaseLogo } from '../DatabaseLogo';

const placeholder: Record<Database, string> = {
  pubmed: `("Pacemaker, Artificial"[Mesh] OR "cardiac pacemaker"[tiab] OR "IPG"[tiab])
AND ("Bradycardia"[Mesh] OR "Heart Block"[Mesh])...`,
  openalex: `("Pacemaker, Artificial"[Mesh] OR "cardiac pacemaker"[tiab] OR "IPG"[tiab])
AND ("Bradycardia"[Mesh] OR "Heart Block"[Mesh])...`,
};

export const DatabaseResultsTab: FC<{
  database: Database;
  query: string;
  onQueryChange: (query: string) => void;
  onRunSearchesClick: () => void;
  isSearchPending: boolean;
}> = ({
  database,
  query,
  onQueryChange,
  onRunSearchesClick,
  isSearchPending,
}) => {
  const { literatureReview_id } = useParams();
  const { literatureReviewSearches, isPending } =
    useLiteratureReviewSearchesService(literatureReview_id);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 50,
  });

  // Filter data for the specific database
  const filteredData = useMemo(() => {
    if (!literatureReviewSearches) return [];

    const search = literatureReviewSearches.find(s => s.source === database);
    return search?.works || [];
  }, [literatureReviewSearches, database]);

  const columns = useDatabaseColumns(database);

  return (
    <FlexCol className="w-full h-full gap-4">
      <FlexCol p="3" gap="3" br="lg" className="bg-neutral-50">
        <FlexRow alignItems="center" justifyContent="between">
          <FlexCol className="items-start justify-center w-full gap-1">
            <DatabaseLogo
              database={database}
              className={cn(database === 'openalex' ? 'h-9' : 'h-8')}
            />
            <Text className="text-sm font-normal">
              Max 1000 results per query
            </Text>
          </FlexCol>
          <Button onClick={onRunSearchesClick} loading={isSearchPending}>
            <Search />
            Run searches
          </Button>
        </FlexRow>
        <Textarea
          rows={4}
          className="bg-white h-[100px]"
          value={query}
          onChange={e => onQueryChange(e.target.value)}
          placeholder={placeholder[database]}
        />
      </FlexCol>

      {filteredData.length > 0 && (
        <DataTable
          columns={columns}
          data={filteredData}
          isLoading={isPending}
          headerStyle="darker"
          grid="bordered"
          options={{
            getPaginationRowModel: getPaginationRowModel(),
            onPaginationChange: setPagination,
            autoResetPageIndex: false,
            state: {
              pagination,
            },
          }}
        />
      )}
    </FlexCol>
  );
};
